import ShipmentLogbook from './ShipmentLogbook';
import { useLocation } from 'wouter';
import useUserStore from 'common/src/stores/useUserStore';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import ShipmentMarketPrices from './ShipmentMarketPrices';
import ShipmentBaseTracking from './ShipmentBaseTracking';
import ShipmentCasesTab from './ShipmentCasesTab';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from '@nextui-org/react';
import type { ShipmentHeaderUpdate } from 'common/src/api/shipments';
import type {
  Shipment,
  ShipmentBox,
  ShipmentTrackingEvent,
  ShipmentTrackingEventRevision,
  ShipmentTrackingSummary,
  BoxFormatMapping,
} from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';
import type { ReactNode } from 'react';
import { Alert, ConfirmModal, LoadingState, StatusChip } from '../ui';
import type { ExporterDivision } from 'common/src/api/exporter-divisions';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  formatLocalEventTime,
  getLocalEventTime,
} from '../../utils/tracking-local-time';
import {
  shipmentHeaderFields,
  type ShipmentDetailsTab,
  type ShipmentHeaderForm,
} from '../../utils/shipment-header-form';
import {
  countryCodeToFlag,
  getTrackingEventCalendarDayGap,
  getTrackingEventElapsedLabel,
  getTrackingEventLabel,
  getTrackingEventSequence,
  normalizeIsoCountryCode,
  normalizeUnLocode,
} from '../../utils/shipment-tracking-display';
import {
  arrivedTrackingEvents,
  inTransitTrackingEvents,
} from '../../utils/shipment-logistics';

export interface ShipmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  referenceNumber: string;
  shipment: Shipment | null;
  boxes: ShipmentBox[];
  headerForm: ShipmentHeaderForm;
  trackingJourneys: ShipmentTrackingSummary[];
  trackingEvents: ShipmentTrackingEvent[];
  trackingEventRevisions: ShipmentTrackingEventRevision[];
  refreshingTrackingJourneyIds: ReadonlySet<string>;
  selectedTab: ShipmentDetailsTab;
  isLoading: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  isSaving: boolean;
  usesFrutic: boolean;
  onTabChange: (tab: ShipmentDetailsTab) => void;
  onHeaderChange: (key: keyof ShipmentHeaderUpdate, value: string) => void;
  onSave: () => Promise<void>;
  /** Divisions of the exporter; the selector only shows with more than one. */
  divisions?: ExporterDivision[];
  onDivisionChange?: (divisionId: number) => Promise<void>;
  isChangingDivision?: boolean;
  /** Official receivers of the exporter, to remediate an unmapped Cliente in place. */
  receiverOptions?: Array<{ id: number; name: string }>;
  onMapReceiver?: (exporterReceiverId: number) => Promise<void>;
  /** Creates an official receiver with that name and returns its id. */
  onCreateReceiver?: (name: string) => Promise<number>;
  isMappingReceiver?: boolean;
  onRefreshTrackingJourney: (id: string) => Promise<void>;
  getReceiverLabel: (box: ShipmentBox) => string | undefined;
  priceUser?: { id: number; name: string };
  onLogbookChanged?: () => void;
  onFormatMapped?: (mapping: BoxFormatMapping) => void;
}

// One row of the General tab: title and help on the left, fields on the right.
const HeaderSection = ({
  title,
  description,
  aside,
  className = '',
  children,
}: {
  title: string;
  description: string;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <section
    aria-label={title}
    className={`grid grid-cols-1 gap-4 lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-8 ${className}`}
  >
    <div className='flex flex-col gap-3'>
      <div>
        <h4 className='font-semibold'>{title}</h4>
        <p className='text-sm text-default-500'>{description}</p>
      </div>
      {aside}
    </div>
    <div className='min-w-0'>{children}</div>
  </section>
);

// Header and tracking requests remain caller-owned; pricing uses its shared flow.
// The sheet does not import the shipment page, its filters or its stores.
// Route-card date at the place's local time. On narrow screens the time goes
// on its own line so the separator never dangles.
const LocalRouteTime = ({
  value,
  timeZone,
  showTime,
}: {
  value?: string | null;
  timeZone?: string | null;
  showTime: boolean;
}) => {
  const local = getLocalEventTime(value, timeZone);
  if (!local) return <>—</>;
  if (!showTime || !local.time) return <>{local.date}</>;
  return (
    <>
      <span className='whitespace-nowrap'>{local.date}</span>
      <span className='hidden sm:inline'> · </span>
      <span className='block whitespace-nowrap sm:inline'>
        {local.time} {local.zone}
      </span>
    </>
  );
};

const ShipmentDetailsModal = ({
  isOpen: isDetailsModalOpen,
  onClose: handleClose,
  referenceNumber: selectedReferenceNumber,
  shipment: selectedShipment,
  boxes: selectedShipmentBoxes,
  headerForm: shipmentHeaderForm,
  trackingJourneys: selectedTrackingJourneys,
  trackingEvents: selectedTrackingEvents,
  trackingEventRevisions: selectedTrackingEventRevisions,
  refreshingTrackingJourneyIds,
  selectedTab: selectedDetailsTab,
  isLoading: isDetailsLoading,
  loadError,
  onRetry,
  isSaving: isSavingShipmentHeader,
  usesFrutic,
  onTabChange: setSelectedDetailsTab,
  onHeaderChange: handleShipmentHeaderChange,
  onSave: handleSaveShipmentHeader,
  divisions = [],
  onDivisionChange,
  isChangingDivision = false,
  receiverOptions = [],
  onMapReceiver,
  onCreateReceiver,
  isMappingReceiver = false,
  onRefreshTrackingJourney: handleRefreshTrackingJourney,
  getReceiverLabel: getDisplayReceiverName,
  priceUser,
  onFormatMapped,
  onLogbookChanged,
}: ShipmentDetailsModalProps) => {
  const { t, currentLanguage } = useTranslation('shipments');
  const { userProfile } = useUserStore();
  const [, setLocation] = useLocation();
  const canOpenCases = Boolean(userProfile && (
    userProfile.roles.some(role => role === 'admin' || role === 'superadmin') ||
    ['claims', 'shipments-details'].every(key => userProfile.permissions?.some(p => p.sidebarItemKey === key && p.enabled))
  ));
  const logbookBusyRef = useRef(false);
  const [isLogbookSaving, setIsLogbookSaving] = useState(false);
  const [logbookShipmentId, setLogbookShipmentId] = useState<number | null>(
    null
  );
  const [activityVersion, setActivityVersion] = useState(0);
  const handleLogbookBusyChange = useCallback((busy: boolean) => {
    logbookBusyRef.current = busy;
    setIsLogbookSaving(busy);
  }, []);
  const priceBusyRef = useRef(false);
  const [isPriceSaving, setIsPriceSaving] = useState(false);
  const handlePriceBusyChange = useCallback((busy: boolean) => {
    priceBusyRef.current = busy;
    setIsPriceSaving(busy);
  }, []);
  const [priceShipmentId, setPriceShipmentId] = useState<number | null>(null);
  const [isPriceRegistrationOpen, setIsPriceRegistrationOpen] = useState(false);
  const shipmentId = selectedShipment?.id;
  const [pendingDivisionId, setPendingDivisionId] = useState<number | null>(
    null
  );
  const [divisionError, setDivisionError] = useState<string | null>(null);
  const [receiverError, setReceiverError] = useState<string | null>(null);
  useEffect(() => {
    setPendingDivisionId(null);
    setDivisionError(null);
    setReceiverError(null);
  }, [isDetailsModalOpen, shipmentId]);
  // Database messages (constraint names, PostgREST codes, English driver
  // text) are not for users; messages raised by our own functions pass through.
  const friendlyError = (error: unknown, fallback: string) => {
    const message = error instanceof Error ? error.message : '';
    const technical =
      /duplicate key|violates|constraint|permission denied|row-level security|PGRST|does not exist|syntax error|exporter_receiver_id \d+|Access denied|Not authenticated/i;
    return message && !technical.test(message) ? t(message) : fallback;
  };
  const savedReceiverName = (selectedShipment?.receiver || '').trim();
  const receiverUnmapped =
    !!selectedShipment &&
    !selectedShipment.exporter_receiver_id &&
    !!savedReceiverName;
  const receiverDrifted =
    !!selectedShipment?.exporter_receiver_id &&
    (shipmentHeaderForm.receiver || '').trim().toUpperCase() !==
      savedReceiverName.toUpperCase();
  const mapReceiver = async (exporterReceiverId: number) => {
    if (!onMapReceiver || !exporterReceiverId) return;
    setReceiverError(null);
    try {
      await onMapReceiver(exporterReceiverId);
    } catch (error) {
      setReceiverError(friendlyError(error, t('No se pudo mapear el cliente.')));
    }
  };
  const createAndMapReceiver = async () => {
    if (!onCreateReceiver || !savedReceiverName) return;
    setReceiverError(null);
    try {
      const id = await onCreateReceiver(savedReceiverName);
      await mapReceiver(id);
    } catch (error) {
      setReceiverError(friendlyError(error, t('No se pudo crear el cliente.')));
    }
  };
  const pendingDivision = divisions.find((d) => d.id === pendingDivisionId);
  const confirmDivisionChange = async () => {
    if (pendingDivisionId === null || !onDivisionChange) return;
    setDivisionError(null);
    try {
      await onDivisionChange(pendingDivisionId);
    } catch (error) {
      setDivisionError(
        friendlyError(error, t('No se pudo cambiar la división.'))
      );
    } finally {
      setPendingDivisionId(null);
    }
  };
  // Field groups of the General tab; anything not listed lands in "Otros".
  const sectionKeys: Record<
    'despacho' | 'tracking' | 'transport' | 'route',
    Array<keyof ShipmentHeaderUpdate>
  > = {
    despacho: ['reference_number', 'receiver'],
    tracking: ['container_number', 'awb_number'],
    transport: ['ship_name', 'ship_type', 'ship_etd', 'ship_eta'],
    route: [
      'ship_origin_port',
      'ship_origin_country',
      'ship_destination_port',
      'ship_destination_country',
    ],
  };
  const groupedKeys = new Set<string>(Object.values(sectionKeys).flat());
  const otherHeaderFields = shipmentHeaderFields.filter(
    (field) => !groupedKeys.has(field.key)
  );
  const fieldLabels: Partial<Record<keyof ShipmentHeaderUpdate, string>> = {
    ship_type: 'Tipo nave',
    ship_origin_port: 'Puerto origen',
    ship_origin_country: 'País origen',
    ship_destination_port: 'Puerto destino',
    ship_destination_country: 'País destino',
    packing_etd: 'Fecha despacho packing',
  };
  const trackedIdentifier = selectedTrackingJourneys[0]?.identifier_type;
  const shipTypeKey = (shipmentHeaderForm.ship_type || '')
    .trim()
    .toUpperCase()
    .charAt(0);
  const transit = (() => {
    const etd = shipmentHeaderForm.ship_etd;
    const eta = shipmentHeaderForm.ship_eta;
    if (!etd || !eta) return null;
    const from = new Date(`${etd.slice(0, 10)}T12:00:00`);
    const to = new Date(`${eta.slice(0, 10)}T12:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    const days = Math.round((to.getTime() - from.getTime()) / 86400000);
    if (days < 0) return null;
    const locale =
      currentLanguage === 'es'
        ? 'es-CL'
        : currentLanguage === 'zh'
          ? 'zh-CN'
          : 'en-US';
    const format = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return { days, from: format.format(from), to: format.format(to) };
  })();
  const renderHeaderField = (
    key: keyof ShipmentHeaderUpdate,
    options?: { hint?: ReactNode; tracked?: boolean }
  ) => {
    const field = shipmentHeaderFields.find((item) => item.key === key);
    if (!field) return null;
    const label = t(fieldLabels[key] ?? field.label);
    const disabled = usesFrutic || isSavingShipmentHeader;
    return (
      <div key={key} className='flex flex-col gap-1'>
        <div className='flex items-baseline justify-between gap-2'>
          <span className='text-sm font-medium text-default-600'>{label}</span>
          {options?.hint}
        </div>
        {key === 'ship_type' ? (
          <Select
            aria-label={label}
            radius='sm'
            placeholder={t('Seleccionar...')}
            selectedKeys={
              ['M', 'A', 'T'].includes(shipTypeKey) ? [shipTypeKey] : []
            }
            onChange={(e) =>
              handleShipmentHeaderChange('ship_type', e.target.value)
            }
            isDisabled={disabled}
          >
            <SelectItem key='M'>{t('Marítimo')}</SelectItem>
            <SelectItem key='A'>{t('Aéreo')}</SelectItem>
            <SelectItem key='T'>{t('Terrestre')}</SelectItem>
          </Select>
        ) : (
          <Input
            aria-label={label}
            type={field.type || 'text'}
            placeholder='—'
            value={shipmentHeaderForm[key]}
            onValueChange={(value) => handleShipmentHeaderChange(key, value)}
            radius='sm'
            variant={options?.tracked ? 'bordered' : 'flat'}
            color={options?.tracked ? 'success' : 'default'}
            isDisabled={disabled}
          />
        )}
      </div>
    );
  };
  const trackingHint = (type: 'container' | 'awb', fallback?: string) =>
    trackedIdentifier === type ? (
      <span className='text-xs font-medium text-success'>
        {t('Rastreando')}
      </span>
    ) : fallback ? (
      <span className='text-xs text-default-400'>{fallback}</span>
    ) : null;
  useEffect(() => {
    setIsPriceRegistrationOpen(false);
  }, [isDetailsModalOpen, shipmentId, priceUser?.id]);
  useEffect(() => {
    if (!isDetailsModalOpen || !shipmentId) setLogbookShipmentId(null);
    else if (selectedDetailsTab === 'logbook') setLogbookShipmentId(shipmentId);
    else
      setLogbookShipmentId((current) =>
        current === shipmentId ? current : null
      );
  }, [isDetailsModalOpen, shipmentId, selectedDetailsTab]);
  const canUsePrices = Boolean(priceUser);
  useEffect(() => {
    if (!isDetailsModalOpen || !canUsePrices || !shipmentId) {
      setPriceShipmentId(null);
      if (isDetailsModalOpen && selectedDetailsTab === 'prices')
        setSelectedDetailsTab('general');
    } else if (selectedDetailsTab === 'prices') {
      setPriceShipmentId(shipmentId);
    } else {
      setPriceShipmentId((current) =>
        current === shipmentId ? current : null
      );
    }
  }, [
    isDetailsModalOpen,
    canUsePrices,
    shipmentId,
    selectedDetailsTab,
    setSelectedDetailsTab,
  ]);
  const closeSheet = () => {
    if (!priceBusyRef.current && !logbookBusyRef.current) handleClose();
  };
  return (
    <Modal
      isOpen={isDetailsModalOpen}
      onClose={closeSheet}
      isDismissable={
        !isPriceSaving && !isSavingShipmentHeader && !isLogbookSaving
      }
      isKeyboardDismissDisabled={
        isPriceSaving || isSavingShipmentHeader || isLogbookSaving
      }
      hideCloseButton={
        isPriceSaving || isSavingShipmentHeader || isLogbookSaving
      }
      placement='center'
      size='5xl'
      scrollBehavior='inside'
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className='flex flex-col gap-1'>
              <h3 className='text-xl font-semibold'>
                {t('Detalles del Despacho')}: {selectedReferenceNumber}
              </h3>
              <div className='text-sm text-default-500 flex items-center gap-2'>
                {isDetailsLoading ? (
                  <Spinner size='sm' color='default' />
                ) : null}
                <span>
                  {selectedDetailsTab === 'tracking' &&
                  selectedTrackingJourneys.length === 0
                    ? t('Datos base del despacho')
                    : selectedDetailsTab === 'tracking'
                      ? `${selectedTrackingJourneys.length} ${t(
                          selectedTrackingJourneys.length === 1
                            ? 'seguimiento activo'
                            : 'seguimientos activos'
                        )}`
                      : `${selectedShipmentBoxes.reduce(
                          (acc, box) => acc + (box.quantity || 0),
                          0
                        )} ${t('bultos en total')}`}
                </span>
              </div>
            </ModalHeader>
            {/* Scrollbar always visible so the sections below the fold are evident. */}
            <ModalBody className='overflow-y-scroll [scrollbar-gutter:stable] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-track]:bg-transparent'>
              {isDetailsLoading ? (
                <LoadingState label={t('Cargando bultos...')} />
              ) : loadError ? (
                <Alert variant='danger' showIcon>
                  <p>{loadError}</p>
                  <Button
                    className='mt-2'
                    size='sm'
                    variant='flat'
                    onPress={onRetry}
                  >
                    {t('Reintentar')}
                  </Button>
                </Alert>
              ) : (
                <Tabs
                  aria-label={t('Secciones del despacho')}
                  classNames={{
                    base: "sticky top-0 z-20 w-full max-w-full bg-content1 pb-2 before:absolute before:inset-x-0 before:-top-2 before:h-2 before:bg-content1 before:content-['']",
                    tabList: 'max-w-full overflow-x-auto',
                    tab: 'w-auto shrink-0',
                  }}
                  destroyInactiveTabPanel={false}
                  selectedKey={selectedDetailsTab}
                  disabledKeys={
                    isLogbookSaving
                      ? ['general', 'tracking', 'packing-list', 'prices', 'cases']
                      : isPriceSaving
                        ? ['general', 'tracking', 'packing-list', 'logbook', 'cases']
                        : isSavingShipmentHeader
                          ? ['prices', 'logbook']
                          : []
                  }
                  onSelectionChange={(key) => {
                    if (
                      !priceBusyRef.current &&
                      !logbookBusyRef.current &&
                      !(
                        isSavingShipmentHeader &&
                        (key === 'prices' || key === 'logbook')
                      )
                    )
                      setSelectedDetailsTab(String(key) as ShipmentDetailsTab);
                  }}
                >
                  <Tab key='general' title={t('General')}>
                    <div className='flex flex-col divide-y divide-default-200 pt-4'>
                      <HeaderSection
                        title={t('Despacho')}
                        description={
                          divisions.length > 1
                            ? t(
                                'Al cambiar la división, las liquidaciones del despacho se mueven con él.'
                              )
                            : t('Referencia y cliente del despacho.')
                        }
                        className='pb-6'
                      >
                        {divisionError ? (
                          <Alert variant='danger' showIcon>
                            <p>{divisionError}</p>
                          </Alert>
                        ) : null}
                        <div
                          className={`grid grid-cols-1 gap-4 md:grid-cols-3 ${
                            divisionError ? 'mt-4' : ''
                          }`}
                        >
                          {divisions.length > 1 && selectedShipment ? (
                            <div className='flex flex-col gap-1'>
                              <span className='text-sm font-medium text-default-600'>
                                {t('División')}
                              </span>
                              <Select
                                aria-label={t('División')}
                                radius='sm'
                                selectedKeys={
                                  selectedShipment.division_id
                                    ? [String(selectedShipment.division_id)]
                                    : []
                                }
                                onChange={(e) => {
                                  const id = Number(e.target.value);
                                  if (id && id !== selectedShipment.division_id)
                                    setPendingDivisionId(id);
                                }}
                                isDisabled={
                                  usesFrutic ||
                                  isSavingShipmentHeader ||
                                  isChangingDivision ||
                                  !onDivisionChange
                                }
                              >
                                {divisions.map((division) => (
                                  <SelectItem key={division.id}>
                                    {division.name}
                                  </SelectItem>
                                ))}
                              </Select>
                            </div>
                          ) : null}
                          {renderHeaderField('reference_number')}
                          {renderHeaderField('receiver', {
                            hint: receiverDrifted ? (
                              <span className='text-xs text-warning-600 dark:text-warning-400'>
                                {t('Se desvinculará al guardar')}
                              </span>
                            ) : null,
                          })}
                        </div>
                        {receiverUnmapped ? (
                          <Alert variant='warning' showIcon className='mt-4'>
                            <p className='font-medium'>
                              {t('Cliente sin mapear')}
                            </p>
                            <p className='text-sm'>
                              {t(
                                '"{{receiver}}" no está asociado a un cliente oficial. Al asociarlo se aplica a todos los despachos y liquidaciones con ese nombre, en todas las temporadas.',
                                { receiver: savedReceiverName }
                              )}
                            </p>
                            {receiverError ? (
                              <p className='mt-1 text-sm text-danger'>
                                {receiverError}
                              </p>
                            ) : null}
                            <div className='mt-3 flex flex-col gap-2 sm:flex-row sm:items-center'>
                              <Select
                                aria-label={t('Cliente oficial')}
                                size='sm'
                                radius='sm'
                                className='sm:max-w-xs'
                                placeholder={t('Seleccionar cliente')}
                                selectedKeys={[]}
                                isDisabled={
                                  isMappingReceiver ||
                                  usesFrutic ||
                                  !onMapReceiver
                                }
                                onChange={(e) =>
                                  void mapReceiver(Number(e.target.value))
                                }
                              >
                                {receiverOptions.map((receiver) => (
                                  <SelectItem key={receiver.id}>
                                    {receiver.name}
                                  </SelectItem>
                                ))}
                              </Select>
                              {onCreateReceiver ? (
                                <Button
                                  size='sm'
                                  variant='flat'
                                  isLoading={isMappingReceiver}
                                  isDisabled={usesFrutic}
                                  onPress={() => void createAndMapReceiver()}
                                >
                                  {t('Crear cliente "{{receiver}}"', {
                                    receiver: savedReceiverName,
                                  })}
                                </Button>
                              ) : null}
                            </div>
                          </Alert>
                        ) : null}
                        {usesFrutic && divisions.length > 1 ? (
                          <p className='mt-2 text-xs text-default-500'>
                            {t(
                              'Los despachos sincronizados desde Frutic no cambian de división desde esta vista.'
                            )}
                          </p>
                        ) : null}
                        <ConfirmModal
                          isOpen={pendingDivisionId !== null}
                          onClose={() => {
                            if (!isChangingDivision) setPendingDivisionId(null);
                          }}
                          onConfirm={() => void confirmDivisionChange()}
                          title={t('¿Mover el despacho a {{division}}?', {
                            division: pendingDivision?.name ?? '',
                          })}
                          confirmLabel={t('Mover')}
                          isLoading={isChangingDivision}
                        >
                          <p className='text-sm text-default-500'>
                            {t(
                              'Las liquidaciones con la misma referencia se moverán también. Si ya existe un despacho con esta referencia en esa división, el cambio se rechaza.'
                            )}
                          </p>
                        </ConfirmModal>
                      </HeaderSection>
                      <HeaderSection
                        title={t('Identificadores de seguimiento')}
                        description={t(
                          'El contenedor (marítimo) o el AWB (aéreo) habilita el seguimiento automático del despacho.'
                        )}
                        className='py-6'
                      >
                        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                          {renderHeaderField('container_number', {
                            tracked: trackedIdentifier === 'container',
                            hint: trackingHint('container'),
                          })}
                          {renderHeaderField('awb_number', {
                            tracked: trackedIdentifier === 'awb',
                            hint: trackingHint('awb', t('Solo aéreo')),
                          })}
                        </div>
                      </HeaderSection>
                      <HeaderSection
                        title={t('Transporte')}
                        description={t(
                          'Nave, fechas estimadas y ruta. Las fechas se actualizan solas cuando el seguimiento está activo.'
                        )}
                        aside={
                          <>
                            {selectedTrackingJourneys.length > 0 ? (
                              <Alert variant='success' showIcon>
                                <p className='text-sm'>
                                  {t(
                                    'Trackeado. Revisa la pestaña Seguimiento para ver los datos actualizados.'
                                  )}
                                </p>
                                <Button
                                  size='sm'
                                  variant='flat'
                                  className='mt-2'
                                  onPress={() =>
                                    setSelectedDetailsTab('tracking')
                                  }
                                >
                                  {t('Ver seguimiento')}
                                </Button>
                              </Alert>
                            ) : null}
                            {transit ? (
                              <div className='rounded-medium border border-default-200 p-4'>
                                <p className='text-xs uppercase tracking-wide text-default-500'>
                                  {t('Tránsito estimado')}
                                </p>
                                <p>
                                  <span className='text-2xl font-semibold'>
                                    {transit.days}
                                  </span>{' '}
                                  <span className='text-sm text-default-500'>
                                    {t('días')}
                                  </span>
                                </p>
                                <p className='text-xs text-default-500'>
                                  {transit.from} → {transit.to}
                                </p>
                              </div>
                            ) : null}
                          </>
                        }
                        className='py-6'
                      >
                        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                          {sectionKeys.transport.map((key) =>
                            renderHeaderField(key)
                          )}
                        </div>
                        <div className='my-4 border-t border-dashed border-default-200' />
                        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                          {sectionKeys.route.map((key) =>
                            renderHeaderField(key)
                          )}
                        </div>
                      </HeaderSection>
                      <HeaderSection
                        title={t('Otros')}
                        description={t('Datos comerciales y de packing.')}
                        className='pt-6'
                      >
                        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                          {otherHeaderFields.map((field) =>
                            renderHeaderField(field.key)
                          )}
                        </div>
                        {usesFrutic ? (
                          <p className='mt-4 text-sm text-default-500'>
                            {t(
                              'Los despachos sincronizados desde Frutic no se editan desde esta vista.'
                            )}
                          </p>
                        ) : null}
                      </HeaderSection>
                    </div>
                  </Tab>
                  <Tab
                    key='tracking'
                    title={
                      <span className='inline-flex items-center gap-1.5'>
                        {selectedTrackingJourneys.length > 0 && (
                          <span className='h-1.5 w-1.5 rounded-full bg-success' />
                        )}
                        {t('Seguimiento')}
                      </span>
                    }
                  >
                    {selectedTrackingJourneys.length > 0 ? (
                      <div className='flex flex-col gap-5 pt-4'>
                        {selectedTrackingJourneys.map((journey) => {
                          const journeyEvents = selectedTrackingEvents
                            .filter(
                              (event) =>
                                event.tracking_journey_id === journey.id
                            )
                            .sort(
                              (a, b) =>
                                new Date(a.occurred_at).getTime() -
                                  new Date(b.occurred_at).getTime() ||
                                getTrackingEventSequence(a.event_type) -
                                  getTrackingEventSequence(b.event_type)
                            );
                          const arrivalEstimateRevisions =
                            selectedTrackingEventRevisions
                              .filter(
                                (revision) =>
                                  revision.tracking_journey_id === journey.id &&
                                  revision.is_estimated &&
                                  ['ARRV', 'ARRIVED'].includes(
                                    revision.event_type.toUpperCase()
                                  )
                              )
                              .sort(
                                (a, b) =>
                                  new Date(a.last_seen_at).getTime() -
                                    new Date(b.last_seen_at).getTime() ||
                                  new Date(a.first_seen_at).getTime() -
                                    new Date(b.first_seen_at).getTime()
                              );
                          const hasConfirmedArrival = journeyEvents.some(
                            (event) =>
                              !event.is_estimated &&
                              ['ARRV', 'ARRIVED'].includes(
                                event.event_type.toUpperCase()
                              )
                          );
                          const originLocode = normalizeUnLocode(
                            journey.origin_locode
                          );
                          const destinationLocode = normalizeUnLocode(
                            journey.destination_locode
                          );
                          const originCountryCode =
                            originLocode?.slice(0, 2) ||
                            normalizeIsoCountryCode(
                              shipmentHeaderForm.ship_origin_country
                            );
                          const destinationCountryCode =
                            destinationLocode?.slice(0, 2) ||
                            normalizeIsoCountryCode(
                              shipmentHeaderForm.ship_destination_country
                            );
                          const originFlag = originCountryCode
                            ? countryCodeToFlag(originCountryCode)
                            : null;
                          const destinationFlag = destinationCountryCode
                            ? countryCodeToFlag(destinationCountryCode)
                            : null;
                          const originName =
                            journey.origin_name ||
                            shipmentHeaderForm.ship_origin_port ||
                            t('Origen');
                          const destinationName =
                            journey.destination_name ||
                            shipmentHeaderForm.ship_destination_port ||
                            (journey.destination_locode
                              ? journey.destination_locode.slice(2)
                              : t('Destino'));
                          const departureDate =
                            journey.live_etd || shipmentHeaderForm.ship_etd;
                          const arrivalDate =
                            journey.live_eta || shipmentHeaderForm.ship_eta;
                          // Air events carry real local times; ShipsGo sea
                          // events only have a day (time is a 12:00 filler).
                          const showEventTime = journey.mode === 'air';
                          const departureTimeZone = (
                            journeyEvents.find((event) =>
                              ['DEP', 'DEPA'].includes(
                                event.event_type.toUpperCase()
                              )
                            ) ?? journeyEvents[0]
                          )?.location_timezone;
                          const arrivalTimeZone = (
                            [...journeyEvents]
                              .reverse()
                              .find((event) =>
                                ['ARR', 'ARRV'].includes(
                                  event.event_type.toUpperCase()
                                )
                              ) ?? journeyEvents[journeyEvents.length - 1]
                          )?.location_timezone;
                          const latestActualJourneyEvent = [...journeyEvents]
                            .filter((event) => !event.is_estimated)
                            .sort(
                              (a, b) =>
                                new Date(b.occurred_at).getTime() -
                                  new Date(a.occurred_at).getTime() ||
                                getTrackingEventSequence(b.event_type) -
                                  getTrackingEventSequence(a.event_type)
                            )[0];
                          const latestActualJourneyEventType =
                            latestActualJourneyEvent?.event_type.toUpperCase();
                          const journeyStatusLabel = latestActualJourneyEvent
                            ? latestActualJourneyEvent.description?.trim() ||
                              t(
                                getTrackingEventLabel(
                                  latestActualJourneyEvent.event_type
                                )
                              )
                            : journey.observation_status ===
                                  'container_returned' ||
                                journey.provider_container_status ===
                                  'EMPTY_RETURN'
                              ? t('Contenedor vacío devuelto')
                              : t('Actualizando seguimiento');
                          const journeyStatusColor =
                            journey.observation_status ===
                              'container_returned' ||
                            journey.provider_container_status ===
                              'EMPTY_RETURN' ||
                            (latestActualJourneyEventType &&
                              arrivedTrackingEvents.has(
                                latestActualJourneyEventType
                              ))
                              ? 'success'
                              : latestActualJourneyEventType &&
                                  inTransitTrackingEvents.has(
                                    latestActualJourneyEventType
                                  )
                                ? 'info'
                                : 'default';
                          const isTrackingRefreshRunning =
                            refreshingTrackingJourneyIds.has(journey.id);
                          const canRefreshTracking = Boolean(
                            journey.provider_tracking_id &&
                              !['container_returned', 'closed'].includes(
                                journey.observation_status || ''
                              ) &&
                              (journey.mode === 'ocean'
                                ? ['active', 'completed'].includes(
                                    journey.status
                                  )
                                : journey.status === 'active')
                          );

                          return (
                            <div
                              key={journey.id}
                              className='flex flex-col gap-5 rounded-medium border border-default-200 p-4'
                            >
                              <div className='flex flex-wrap items-start justify-between gap-3'>
                                <div>
                                  <p className='text-xs font-semibold uppercase tracking-[0.14em] text-default-400'>
                                    {journey.identifier_type === 'container'
                                      ? t('Contenedor')
                                      : 'AWB'}
                                  </p>
                                  <p className='mt-1 font-mono text-lg font-semibold text-foreground'>
                                    {journey.identifier_value}
                                  </p>
                                </div>
                                <StatusChip tone={journeyStatusColor}>
                                  {journeyStatusLabel}
                                </StatusChip>
                              </div>

                              <div className='grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 rounded-xl bg-default-50 px-4 py-4'>
                                <div className='min-w-0'>
                                  <p className='text-xs font-medium uppercase tracking-wide text-default-400'>
                                    {t('Origen')}
                                  </p>
                                  <p
                                    className='mt-1 flex min-w-0 items-center gap-1.5 text-sm font-semibold text-foreground'
                                    title={originName}
                                  >
                                    {originFlag ? (
                                      <span className='text-lg leading-none'>
                                        {originFlag}
                                      </span>
                                    ) : null}
                                    <span className='truncate'>
                                      {originName}
                                    </span>
                                    {originLocode ? (
                                      <span className='shrink-0 text-xs font-medium text-default-400'>
                                        {originLocode.slice(2)}
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className='mt-1 text-xs text-default-500'>
                                    <LocalRouteTime
                                      value={departureDate}
                                      timeZone={departureTimeZone}
                                      showTime={showEventTime}
                                    />
                                  </p>
                                </div>
                                <div className='flex items-center gap-2 text-default-300'>
                                  <span className='h-px w-8 bg-default-300 md:w-16' />
                                  <span aria-hidden='true'>→</span>
                                </div>
                                <div className='min-w-0 text-right'>
                                  <p className='text-xs font-medium uppercase tracking-wide text-default-400'>
                                    {t('Destino')}
                                  </p>
                                  <p
                                    className='mt-1 flex min-w-0 items-center justify-end gap-1.5 text-sm font-semibold text-foreground'
                                    title={destinationName}
                                  >
                                    {destinationFlag ? (
                                      <span className='text-lg leading-none'>
                                        {destinationFlag}
                                      </span>
                                    ) : null}
                                    <span className='truncate'>
                                      {destinationName}
                                    </span>
                                    {destinationLocode ? (
                                      <span className='shrink-0 text-xs font-medium text-default-400'>
                                        {destinationLocode.slice(2)}
                                      </span>
                                    ) : null}
                                  </p>
                                  <p className='mt-1 text-xs text-default-500'>
                                    <LocalRouteTime
                                      value={arrivalDate}
                                      timeZone={arrivalTimeZone}
                                      showTime={showEventTime}
                                    />
                                  </p>
                                </div>
                              </div>

                              <div>
                                <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                                  <div>
                                    <h4 className='text-sm font-semibold text-foreground'>
                                      {t('Historial del trayecto')}
                                    </h4>
                                    <p className='mt-0.5 text-xs text-default-500'>
                                      {journeyEvents.length > 0
                                        ? t(
                                            'Hitos confirmados y estimados del viaje'
                                          )
                                        : t(
                                            'Esperando la primera actualización de hitos'
                                          )}
                                    </p>
                                  </div>
                                  <div className='flex shrink-0 flex-wrap items-center gap-2 sm:justify-end'>
                                    {journey.last_sync_succeeded_at ||
                                    journey.updated_at ? (
                                      <span className='text-xs text-default-400'>
                                        {t('Actualizado')}{' '}
                                        {format(
                                          new Date(
                                            journey.last_sync_succeeded_at ||
                                              journey.updated_at ||
                                              ''
                                          ),
                                          'dd MMM, HH:mm',
                                          { locale: es }
                                        )}
                                      </span>
                                    ) : null}
                                    <Button
                                      size='sm'
                                      variant='light'
                                      className='h-7 px-2 text-xs'
                                      startContent={
                                        isTrackingRefreshRunning ? null : (
                                          <Icon icon='tabler:refresh' />
                                        )
                                      }
                                      isLoading={isTrackingRefreshRunning}
                                      isDisabled={
                                        !canRefreshTracking ||
                                        isSavingShipmentHeader ||
                                        refreshingTrackingJourneyIds.size > 0
                                      }
                                      onPress={() =>
                                        handleRefreshTrackingJourney(journey.id)
                                      }
                                    >
                                      {t(
                                        isTrackingRefreshRunning
                                          ? 'Actualizando...'
                                          : 'Actualizar ahora'
                                      )}
                                    </Button>
                                  </div>
                                </div>

                                <div className='rounded-xl border border-default-200 px-4 py-4'>
                                  {journeyEvents.length === 0 ? (
                                    <div className='flex min-h-24 items-center justify-center text-sm text-default-400'>
                                      {t(
                                        'Esperando la primera actualización de hitos'
                                      )}
                                    </div>
                                  ) : (
                                    journeyEvents
                                      .map((event) => {
                                        const eventLocode = normalizeUnLocode(
                                          event.location_locode
                                        );
                                        const eventCountryCode =
                                          eventLocode?.slice(0, 2);
                                        return {
                                          key: event.id,
                                          label:
                                            event.description?.trim() ||
                                            t(
                                              getTrackingEventLabel(
                                                event.event_type
                                              )
                                            ),
                                          location:
                                            event.location_name ||
                                            t('Ubicación no informada'),
                                          locationFlag: eventCountryCode
                                            ? countryCodeToFlag(
                                                eventCountryCode
                                              )
                                            : null,
                                          locationPort: eventLocode?.slice(2),
                                          date: event.occurred_at,
                                          timeZone: event.location_timezone,
                                          localTime: getLocalEventTime(
                                            event.occurred_at,
                                            event.location_timezone
                                          ),
                                          vessel: [
                                            event.vessel_name,
                                            event.voyage_number
                                              ? `${t('Viaje')} ${event.voyage_number}`
                                              : null,
                                          ]
                                            .filter(Boolean)
                                            .join(' · '),
                                          isEstimated:
                                            event.is_estimated ||
                                            event.provider_status?.toUpperCase() ===
                                              'EST',
                                        };
                                      })
                                      .map((event, eventIndex, events) => {
                                        const nextEvent =
                                          events[eventIndex + 1];
                                        const calendarDayGap = nextEvent
                                          ? getTrackingEventCalendarDayGap(
                                              event.date,
                                              nextEvent.date,
                                              event.timeZone,
                                              nextEvent.timeZone
                                            )
                                          : null;
                                        // Air has real times: elapsed
                                        // "4 h 44 min"; sea only has days.
                                        const gapLabel = !nextEvent
                                          ? null
                                          : showEventTime
                                            ? getTrackingEventElapsedLabel(
                                                event.date,
                                                nextEvent.date
                                              )
                                            : calendarDayGap
                                              ? `${calendarDayGap} d`
                                              : null;

                                        return (
                                          <div
                                            key={event.key}
                                            className='relative'
                                          >
                                            {nextEvent ? (
                                              <span
                                                aria-hidden='true'
                                                className={`absolute bottom-[-0.375rem] left-[0.5625rem] top-4 w-0 border-l-2 ${
                                                  nextEvent.isEstimated
                                                    ? 'border-dashed border-default-300'
                                                    : 'border-solid border-success/60'
                                                }`}
                                              />
                                            ) : null}
                                            <div className='grid grid-cols-[1.25rem_minmax(0,1fr)] gap-3 sm:grid-cols-[1.25rem_minmax(0,1fr)_auto]'>
                                              <span
                                                className={`relative z-10 mt-1.5 h-3 w-3 justify-self-center rounded-full border-2 border-content1 ${
                                                  event.isEstimated
                                                    ? 'bg-warning-400 ring-2 ring-warning-100'
                                                    : 'bg-success ring-2 ring-success-100'
                                                }`}
                                              />
                                              <div className='min-w-0'>
                                                <div className='flex min-w-0 flex-wrap items-center gap-2'>
                                                  <p className='text-sm font-semibold text-foreground'>
                                                    {event.label}
                                                  </p>
                                                  <span
                                                    className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                                      event.isEstimated
                                                        ? 'bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400'
                                                        : 'bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400'
                                                    }`}
                                                  >
                                                    {event.isEstimated
                                                      ? t('Estimado')
                                                      : t('Confirmado')}
                                                  </span>
                                                </div>
                                                <p className='mt-0.5 text-[13px] text-default-500'>
                                                  {event.locationFlag ? (
                                                    <span className='mr-1'>
                                                      {event.locationFlag}
                                                    </span>
                                                  ) : null}
                                                  {event.location}
                                                  {event.locationPort
                                                    ? ` · ${event.locationPort}`
                                                    : ''}
                                                  {event.vessel
                                                    ? ` · ${event.vessel}`
                                                    : ''}
                                                </p>
                                              </div>
                                              <p className='col-start-2 whitespace-nowrap text-[13px] text-default-500 sm:col-start-3 sm:pt-px sm:text-right'>
                                                {!event.localTime ? (
                                                  '—'
                                                ) : showEventTime &&
                                                  event.localTime.time ? (
                                                  <>
                                                    {event.localTime.shortDate}
                                                    <span
                                                      aria-hidden='true'
                                                      className='mx-1.5 text-default-300'
                                                    >
                                                      ·
                                                    </span>
                                                    <span className='text-sm font-semibold tabular-nums text-foreground'>
                                                      {event.localTime.time}
                                                    </span>{' '}
                                                    <span className='text-[11px] font-medium uppercase text-default-400'>
                                                      {event.localTime.zone}
                                                    </span>
                                                  </>
                                                ) : (
                                                  event.localTime.date
                                                )}
                                              </p>
                                            </div>
                                            {nextEvent ? (
                                              <div className='flex items-center gap-3 py-3 pl-8'>
                                                <span className='flex-1 border-t border-dashed border-default-200' />
                                                {gapLabel ? (
                                                  <span className='shrink-0 rounded-full bg-default-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-default-500'>
                                                    {gapLabel}
                                                  </span>
                                                ) : null}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })
                                  )}
                                </div>
                              </div>

                              {arrivalEstimateRevisions.length > 0 ? (
                                <div>
                                  <div className='mb-3'>
                                    <h4 className='text-sm font-semibold text-foreground'>
                                      {t('Historial de ETA')}
                                    </h4>
                                    <p className='mt-0.5 text-xs text-default-500'>
                                      {t(
                                        'Cada cambio de ETA informado durante el trayecto'
                                      )}
                                    </p>
                                  </div>
                                  <div className='divide-y divide-default-200 rounded-xl border border-default-200 px-4'>
                                    {arrivalEstimateRevisions.map(
                                      (revision, revisionIndex) => {
                                        const isLatest =
                                          revisionIndex ===
                                          arrivalEstimateRevisions.length - 1;
                                        return (
                                          <div
                                            key={revision.id}
                                            className='flex flex-wrap items-center justify-between gap-3 py-3'
                                          >
                                            <div>
                                              <p className='text-sm font-semibold text-foreground'>
                                                {formatLocalEventTime(
                                                  revision.occurred_at,
                                                  revision.location_timezone,
                                                  showEventTime
                                                )}
                                              </p>
                                              <p className='mt-0.5 text-xs text-default-500'>
                                                {t('Detectado')}{' '}
                                                {format(
                                                  new Date(
                                                    revision.first_seen_at
                                                  ),
                                                  'dd MMM yyyy, HH:mm',
                                                  { locale: es }
                                                )}
                                              </p>
                                            </div>
                                            <Chip
                                              size='sm'
                                              variant='flat'
                                              color={
                                                isLatest && !hasConfirmedArrival
                                                  ? 'warning'
                                                  : 'default'
                                              }
                                            >
                                              {isLatest
                                                ? hasConfirmedArrival
                                                  ? t('Última estimación')
                                                  : t('Vigente')
                                                : t('Anterior')}
                                            </Chip>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <ShipmentBaseTracking shipment={selectedShipment} />
                    )}
                  </Tab>
                  <Tab key='packing-list' title={t('Packing List')}>
                    <div className='pt-4'>
                      <Table
                        isStriped
                        isCompact
                        aria-label={t('Detalles de bultos del despacho')}
                      >
                        <TableHeader>
                          <TableColumn key='receiver'>
                            {t('Cliente')}
                          </TableColumn>
                          <TableColumn key='packing_name'>
                            {t('Packing')}
                          </TableColumn>
                          <TableColumn key='producer_name'>
                            {t('Productor')}
                          </TableColumn>
                          <TableColumn key='variety_real'>
                            {t('Variedad Real')}
                          </TableColumn>
                          <TableColumn key='variety_labeled'>
                            {t('Variedad Etiquetada')}
                          </TableColumn>
                          <TableColumn key='color'>{t('Color')}</TableColumn>
                          <TableColumn key='caliber'>
                            {t('Calibre')}
                          </TableColumn>
                          <TableColumn key='category'>
                            {t('Categoría')}
                          </TableColumn>
                          <TableColumn key='box_format'>
                            {t('Formato')}
                          </TableColumn>
                          <TableColumn key='box_weight'>
                            {t('Peso Caja')}
                          </TableColumn>
                          <TableColumn key='quantity'>
                            {t('Cantidad')}
                          </TableColumn>
                          <TableColumn key='total_kg'>
                            {t('Total Kg')}
                          </TableColumn>
                          <TableColumn key='box_label'>
                            {t('Etiqueta')}
                          </TableColumn>
                          <TableColumn key='container_number'>
                            {t('Contenedor')}
                          </TableColumn>
                          <TableColumn key='fob_price'>
                            {t('Precio FOB')}
                          </TableColumn>
                        </TableHeader>
                        <TableBody
                          emptyContent={t('No hay bultos para mostrar')}
                          items={
                            selectedDetailsTab === 'packing-list'
                              ? selectedShipmentBoxes
                              : []
                          }
                        >
                          {(box) => (
                            <TableRow
                              key={
                                box.id ||
                                `box-${box.reference_number}-${box.producer_name}-${box.variety}`
                              }
                            >
                              <TableCell>
                                <span
                                  className='block max-w-40 truncate'
                                  title={getDisplayReceiverName(box)}
                                >
                                  {getDisplayReceiverName(box) ||
                                    t('Sin cliente')}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span
                                  className='block max-w-40 truncate'
                                  title={box.packing_name}
                                >
                                  {box.packing_name || '-'}
                                </span>
                              </TableCell>
                              <TableCell>{box.producer_name}</TableCell>
                              <TableCell>{box.variety}</TableCell>
                              <TableCell>
                                {
                                  (
                                    box as ShipmentBox & {
                                      labeled_variety?: string;
                                    }
                                  ).labeled_variety
                                }
                              </TableCell>
                              <TableCell>{box.color || ''}</TableCell>
                              <TableCell>{box.caliber}</TableCell>
                              <TableCell>{box.category}</TableCell>
                              <TableCell>{box.box_format}</TableCell>
                              <TableCell>{box.box_weight} kg</TableCell>
                              <TableCell>{box.quantity}</TableCell>
                              <TableCell>
                                {(
                                  (box.quantity || 0) * (box.box_weight || 0)
                                ).toFixed(2)}{' '}
                                kg
                              </TableCell>
                              <TableCell>{box.box_label}</TableCell>
                              <TableCell>
                                {box.container_number || '-'}
                              </TableCell>
                              <TableCell>
                                {box.fob_price !== null &&
                                box.fob_price !== undefined &&
                                String(box.fob_price) !== ''
                                  ? Number(box.fob_price).toFixed(2)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </Tab>
                  {priceUser && selectedShipment ? (
                    <Tab key='prices' title={t('Precios de mercado')}>
                      {(priceShipmentId === selectedShipment.id ||
                        selectedDetailsTab === 'prices') && (
                        <ShipmentMarketPrices
                          key={JSON.stringify([
                            selectedShipment.id,
                            selectedShipment.exporter_id,
                            selectedShipment.species_id,
                            selectedShipment.season,
                            priceUser.id,
                          ])}
                          shipment={selectedShipment}
                          user={priceUser}
                          isOpen={isDetailsModalOpen}
                          isRegistering={isPriceRegistrationOpen}
                          onRegisteringChange={setIsPriceRegistrationOpen}
                          onBusyChange={handlePriceBusyChange}
                          onActivityChanged={() => {
                            setActivityVersion((v) => v + 1);
                            onLogbookChanged?.();
                          }}
                          onFormatMapped={onFormatMapped}
                        />
                      )}
                    </Tab>
                  ) : null}
                  {selectedShipment && (
                    <Tab key='logbook' title={t('Bitácora')}>
                      {(logbookShipmentId === selectedShipment.id ||
                        selectedDetailsTab === 'logbook') && (
                        <ShipmentLogbook
                          key={JSON.stringify([
                            selectedShipment.id,
                            selectedShipment.exporter_id,
                            selectedShipment.season,
                            selectedShipment.species_id,
                          ])}
                          shipment={selectedShipment}
                          isOpen={isDetailsModalOpen}
                          onBusyChange={handleLogbookBusyChange}
                          refreshVersion={activityVersion}
                          onChanged={onLogbookChanged}
                        />
                      )}
                    </Tab>
                  )}
                  {canOpenCases && selectedShipment ? (
                    <Tab key='cases' title={t('Reclamos')}>
                      {selectedDetailsTab === 'cases' && (
                        <ShipmentCasesTab
                          shipment={selectedShipment}
                          onOpen={(path) => {
                            handleClose();
                            setLocation(path);
                          }}
                        />
                      )}
                    </Tab>
                  ) : null}
                </Tabs>
              )}
            </ModalBody>
            {(selectedDetailsTab !== 'prices' || !isPriceRegistrationOpen) && (
              <ModalFooter>
                <Button variant='light' onPress={onClose}>
                  {t('Cerrar')}
                </Button>
                {selectedDetailsTab === 'general' && !usesFrutic ? (
                  <Button
                    color='primary'
                    onPress={handleSaveShipmentHeader}
                    isLoading={isSavingShipmentHeader}
                    isDisabled={
                      !selectedShipment?.id ||
                      !!loadError ||
                      refreshingTrackingJourneyIds.size > 0
                    }
                  >
                    {t('Guardar')}
                  </Button>
                ) : null}
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ShipmentDetailsModal;
